import React, { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { buildEventsObjectiveText } from "./inpatient";
import { normalizeExam2Values } from "./exam2Catalog";
import Exam2SystemsTable from "./Exam2SystemsTable";

const EMPTY_EXAM2 = [];
const filterFindings = createFilterOptions();

const filterWithWriteIn = (options, params) => {
  const filtered = filterFindings(options, params);
  const inputValue = String(params.inputValue || "").trim();
  if (
    inputValue &&
    !options.some((option) => option.toLowerCase() === inputValue.toLowerCase())
  ) {
    filtered.push(inputValue);
  }
  return filtered;
};

/**
 * Events Objective editor: read-only event bullets + Exam or Exam 2 documentation.
 */
const ObjectiveExamEditor = ({
  events = [],
  examFindings = [],
  examResults = new Map(),
  examMode = "exam",
  exam2Systems,
  onSave,
  onCancel,
}) => {
  const isExam2 = examMode === "exam2";
  const systemsProp = exam2Systems || EMPTY_EXAM2;

  const initialDraft = useMemo(() => {
    const draft = {};
    examFindings.forEach((exam) => {
      const existing = examResults instanceof Map ? examResults.get(exam.id) : examResults?.[exam.id];
      draft[exam.id] = existing?.status
        ? {
            status: existing.status,
            finding: existing.finding || "",
          }
        : { status: "", finding: "" };
    });
    return draft;
  }, [examFindings, examResults]);

  const { control, handleSubmit, watch, setValue, reset, getValues } = useForm({
    defaultValues: {
      exams: initialDraft,
      exam2: Object.fromEntries(
        systemsProp.map((system) => [system.id, normalizeExam2Values(system.value)]),
      ),
    },
  });

  useEffect(() => {
    reset({
      exams: initialDraft,
      exam2: Object.fromEntries(
        systemsProp.map((system) => [system.id, normalizeExam2Values(system.value)]),
      ),
    });
    // systemsProp identity is stable via EMPTY_EXAM2; content sync uses the joined key below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialDraft,
    reset,
    systemsProp
      .map((system) => `${system.id}\0${normalizeExam2Values(system.value).join("\u0001")}`)
      .join("\n"),
  ]);

  const examsDraft = watch("exams") || {};

  const submit = handleSubmit(({ exams, exam2 }) => {
    if (isExam2) {
      const nextSystems = systemsProp.map((system) => ({
        ...system,
        value: normalizeExam2Values(exam2?.[system.id]),
      }));
      const objective = buildEventsObjectiveText({
        events,
        examMode: "exam2",
        exam2Systems: nextSystems,
      });
      onSave?.({ exam2Systems: nextSystems, objective });
      return;
    }

    const nextResults = new Map();
    Object.entries(exams || {}).forEach(([examId, row]) => {
      if (!row?.status) return;
      if (row.status === "normal") {
        nextResults.set(examId, { status: "normal" });
        return;
      }
      const finding = String(row.finding || "").trim();
      if (finding) nextResults.set(examId, { status: "abnormal", finding });
    });
    const objective = buildEventsObjectiveText({
      events,
      examFindings,
      examResults: nextResults,
      examMode: "exam",
    });
    onSave?.({ examResults: nextResults, objective });
  });

  // Local draft overlay so typing does not fight the parent systemsProp until Save.
  const exam2TableSystems = useMemo(() => {
    const draft = getValues("exam2") || {};
    return systemsProp.map((system) => ({
      ...system,
      value: normalizeExam2Values(draft[system.id] ?? system.value),
    }));
    // Recompute when committed props change; live edits stay inside Exam2SystemsTable / setValue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    systemsProp
      .map((system) => `${system.id}\0${normalizeExam2Values(system.value).join("\u0001")}`)
      .join("\n"),
  ]);

  return (
    <Stack spacing={3} sx={{ mt: 1 }}>
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Events
        </Typography>
        {events.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No events documented.
          </Typography>
        ) : (
          events.map((event) => {
            const comment = String(event.comment || "").trim();
            return (
              <Typography key={event.id} variant="body1" sx={{ mb: 0.75 }}>
                {`- ${event.name}${comment ? `: ${comment}` : ""}`}
              </Typography>
            );
          })
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
          {isExam2 ? "Exam 2" : "Exam"}
        </Typography>
        {isExam2 ? (
          <Exam2SystemsTable
            systems={exam2TableSystems}
            onChangeSystemValue={(systemId, value) => {
              setValue(`exam2.${systemId}`, value, { shouldDirty: true });
            }}
          />
        ) : examFindings.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No exam items kept on the Plan tab.
          </Typography>
        ) : (
          <Stack spacing={2.5}>
            {examFindings.map((exam) => {
              const status = examsDraft[exam.id]?.status || "";
              const options = Array.isArray(exam.anticipatedFindings)
                ? [...exam.anticipatedFindings].sort((a, b) => a.localeCompare(b))
                : [];
              return (
                <Box key={exam.id}>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                    {exam.name}
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={status || null}
                    onChange={(_, value) => {
                      if (value == null) return;
                      setValue(`exams.${exam.id}.status`, value, { shouldDirty: true });
                      if (value === "normal") {
                        setValue(`exams.${exam.id}.finding`, "", { shouldDirty: true });
                      }
                    }}
                    sx={{ mb: status === "abnormal" ? 1.5 : 0 }}
                  >
                    <ToggleButton value="normal">Normal</ToggleButton>
                    <ToggleButton value="abnormal">Abnormal</ToggleButton>
                  </ToggleButtonGroup>
                  {status === "abnormal" ? (
                    <Controller
                      name={`exams.${exam.id}.finding`}
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          freeSolo
                          selectOnFocus
                          clearOnBlur
                          handleHomeEndKeys
                          options={options}
                          filterOptions={filterWithWriteIn}
                          value={field.value || null}
                          inputValue={field.value || ""}
                          onChange={(_, value) =>
                            field.onChange(typeof value === "string" ? value : value || "")
                          }
                          onInputChange={(_, value, reason) => {
                            if (reason === "reset") return;
                            field.onChange(value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Finding"
                              placeholder="Choose or type a finding"
                              fullWidth
                            />
                          )}
                        />
                      )}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={submit}>
          Save
        </Button>
      </Stack>
    </Stack>
  );
};

export default ObjectiveExamEditor;
